import { memo, useMemo, useState } from 'react'
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Divider,
  IconButton,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import RefreshIcon from '@mui/icons-material/Refresh'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline'
import SendIcon from '@mui/icons-material/Send'
import ScheduleIcon from '@mui/icons-material/Schedule'
import { SchedulerComment } from './Scheduler.data'

const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']

const formatTime = (hour: number): string => {
  const day = DAY_LABELS[Math.floor(hour / 24)]
  const h = Math.floor(hour % 24)
  return `${day} ${h.toString().padStart(2, '0')}:00`
}

const getInitials = (name: string): string => {
  const parts = name.trim().split(/\s+/)
  if (parts.length >= 2)
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
  return name.slice(0, 2).toUpperCase()
}

const timeAgo = (iso: string): string => {
  const diff = Date.now() - new Date(iso).getTime()
  const mins = Math.floor(diff / 60000)
  if (mins < 1) return 'just now'
  if (mins < 60) return `${mins}m ago`
  const hrs = Math.floor(mins / 60)
  if (hrs < 24) return `${hrs}h ago`
  const days = Math.floor(hrs / 24)
  return `${days}d ago`
}

// ─── Comment Card ───────────────────────────────────────────────────

const CommentCard = memo(
  ({
    comment,
    currentUserEmail,
    onResolve,
    onDelete,
    onClick,
  }: {
    comment: SchedulerComment
    currentUserEmail: string
    onResolve: (id: string, resolved: boolean) => void
    onDelete: (id: string) => void
    onClick: (comment: SchedulerComment) => void
  }) => {
    const isOwn =
      comment.authorEmail.toLowerCase() === currentUserEmail.toLowerCase()

    return (
      <Paper
        variant="outlined"
        onClick={() => onClick(comment)}
        sx={{
          p: 1.25,
          borderRadius: 1.5,
          cursor: 'pointer',
          transition: 'all 0.15s',
          opacity: comment.resolved ? 0.6 : 1,
          '&:hover': {
            borderColor: 'primary.main',
            bgcolor: 'rgba(0,65,90,0.02)',
          },
        }}
      >
        {/* Author row */}
        <Box
          sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.75 }}
        >
          <Box
            sx={{
              width: 24,
              height: 24,
              borderRadius: '50%',
              bgcolor: 'primary.main',
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '0.55rem',
              fontWeight: 700,
              flexShrink: 0,
            }}
          >
            {getInitials(comment.authorName)}
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              sx={{ fontSize: '0.68rem', fontWeight: 600, lineHeight: 1.2 }}
              noWrap
            >
              {comment.authorName}
            </Typography>
            <Typography
              sx={{
                fontSize: '0.58rem',
                color: 'text.disabled',
                lineHeight: 1.2,
              }}
            >
              {timeAgo(comment.createdAt)}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
            <Tooltip
              title={comment.resolved ? 'Mark unresolved' : 'Mark resolved'}
            >
              <Checkbox
                checked={comment.resolved}
                onChange={(e) => {
                  e.stopPropagation()
                  onResolve(comment.id, !comment.resolved)
                }}
                size="small"
                sx={{ p: 0.25, '& .MuiSvgIcon-root': { fontSize: 16 } }}
              />
            </Tooltip>
            {isOwn && (
              <Tooltip title="Delete">
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation()
                    onDelete(comment.id)
                  }}
                  sx={{ p: 0.25 }}
                >
                  <DeleteOutlineIcon
                    sx={{ fontSize: 14, color: 'text.disabled' }}
                  />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </Box>

        {/* Batch anchor chip */}
        <Box sx={{ display: 'flex', gap: 0.5, mb: 0.5, flexWrap: 'wrap' }}>
          <Chip
            label={`Week ${comment.weekNumber}`}
            size="small"
            sx={{
              height: 18,
              fontSize: '0.58rem',
              fontWeight: 600,
              bgcolor: 'rgba(0,130,179,0.08)',
              color: '#0082B3',
            }}
          />
          <Chip
            label={comment.productCode}
            size="small"
            sx={{
              height: 18,
              fontSize: '0.58rem',
              fontWeight: 600,
              bgcolor: 'rgba(0,65,90,0.08)',
            }}
          />
          <Chip
            icon={<ScheduleIcon sx={{ fontSize: '10px !important' }} />}
            label={`${comment.resourceName} · ${formatTime(comment.startTime)} – ${formatTime(comment.endTime)}`}
            size="small"
            variant="outlined"
            sx={{
              height: 18,
              fontSize: '0.55rem',
              '& .MuiChip-icon': { ml: 0.25 },
            }}
          />
        </Box>

        {/* Content */}
        <Typography
          sx={{
            fontSize: '0.7rem',
            color: 'text.primary',
            lineHeight: 1.4,
            textDecoration: comment.resolved ? 'line-through' : 'none',
          }}
        >
          {comment.content}
        </Typography>
      </Paper>
    )
  }
)
CommentCard.displayName = 'CommentCard'

// ─── Main Component ─────────────────────────────────────────────────

type SchedulerCommentsProps = {
  comments: SchedulerComment[]
  currentUserEmail: string
  onRefresh: () => void
  onResolve: (commentId: string, resolved: boolean) => void
  onDelete: (commentId: string) => void
  onCommentClick: (comment: SchedulerComment) => void
  isLoading?: boolean
}

export const SchedulerCommentsPanel = memo(
  ({
    comments,
    currentUserEmail,
    onRefresh,
    onResolve,
    onDelete,
    onCommentClick,
    isLoading,
  }: SchedulerCommentsProps) => {
    const [showResolved, setShowResolved] = useState(false)

    const filteredComments = useMemo(() => {
      if (showResolved) return comments
      return comments.filter((c) => !c.resolved)
    }, [comments, showResolved])

    const resolvedCount = comments.filter((c) => c.resolved).length

    return (
      <Box sx={{ width: '100%' }}>
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 1.5,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <ChatBubbleOutlineIcon
              sx={{ fontSize: 16, color: 'primary.main' }}
            />
            <Typography
              sx={{
                fontSize: '0.7rem',
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: 0.8,
                color: 'primary.main',
              }}
            >
              Comments
            </Typography>
            <Chip
              label={comments.length}
              size="small"
              sx={{
                height: 18,
                fontSize: '0.6rem',
                fontWeight: 700,
                minWidth: 24,
              }}
            />
          </Box>
          <Tooltip title="Refresh comments">
            <IconButton size="small" onClick={onRefresh} disabled={isLoading}>
              <RefreshIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        </Box>

        {/* Filter */}
        {resolvedCount > 0 && (
          <Box sx={{ mb: 1 }}>
            <Chip
              label={
                showResolved
                  ? `Hide ${resolvedCount} resolved`
                  : `Show ${resolvedCount} resolved`
              }
              size="small"
              variant="outlined"
              onClick={() => setShowResolved(!showResolved)}
              sx={{ fontSize: '0.6rem', height: 22, cursor: 'pointer' }}
            />
          </Box>
        )}

        {/* Comment list */}
        {filteredComments.length === 0 ? (
          <Paper
            variant="outlined"
            sx={{
              p: 2,
              textAlign: 'center',
              borderStyle: 'dashed',
              borderRadius: 1.5,
            }}
          >
            <ChatBubbleOutlineIcon
              sx={{ fontSize: 28, color: 'text.disabled', mb: 0.5 }}
            />
            <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary' }}>
              No comments yet
            </Typography>
            <Typography
              sx={{ fontSize: '0.62rem', color: 'text.disabled', mt: 0.25 }}
            >
              Click a batch on the Gantt chart to add a comment
            </Typography>
          </Paper>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
            {filteredComments.map((comment) => (
              <CommentCard
                key={comment.id}
                comment={comment}
                currentUserEmail={currentUserEmail}
                onResolve={onResolve}
                onDelete={onDelete}
                onClick={onCommentClick}
              />
            ))}
          </Box>
        )}
      </Box>
    )
  }
)

SchedulerCommentsPanel.displayName = 'SchedulerCommentsPanel'
